import { styled } from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { CircularLoader } from "@similarweb/ui-components";

import { SWReactTable } from "app/components/React/Table/SWReactTable";
import { translate } from "app/filters/i18nFilter";

import { tableColumns } from "../helpers";
import type { TableData, User } from "../types";

import { EmptyState, EmptyStateSubtitle, EmptyStateTextWrapper, EmptyStateTitle } from "./EmptyState";
import { EmptyStateIllustration } from "./EmptyStateIllustration";
import { NoDataFoundIllustration } from "./NoDataFoundIllustration";

const LoadingMoreWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    flex-direction: row;
`;

type SearchResultsDisplayProps = {
    search: string;
    tableData: TableData | null;
    loading: boolean;
    isFetchingNextPage: boolean;
    onItemClick: (user: User) => void;
};

export const SearchResultsDisplay = ({
    search,
    tableData,
    loading,
    isFetchingNextPage,
    onItemClick,
}: SearchResultsDisplayProps) => {
    const hasNoResults = search !== "" && !loading && (!tableData || tableData.Records.length === 0);

    if (search === "") {
        return (
            <EmptyState>
                <EmptyStateIllustration />
                <EmptyStateTextWrapper>
                    <EmptyStateTitle>
                        {translate("Usermanagement.Modal.Impersonate.EmptyState.SearchText")}
                    </EmptyStateTitle>
                    <EmptyStateSubtitle>
                        {translate("Usermanagement.Modal.Impersonate.EmptyState.Subtitle")}
                    </EmptyStateSubtitle>
                </EmptyStateTextWrapper>
            </EmptyState>
        );
    }

    if (hasNoResults) {
        return (
            <EmptyState>
                <NoDataFoundIllustration />
                <EmptyStateTextWrapper>
                    <EmptyStateTitle>{translate("Usermanagement.Modal.Impersonate.NoResults.Title")}</EmptyStateTitle>
                    <EmptyStateSubtitle>
                        {translate("Usermanagement.Modal.Impersonate.NoResults.Subtitle")}
                    </EmptyStateSubtitle>
                </EmptyStateTextWrapper>
            </EmptyState>
        );
    }

    return (
        <>
            <SWReactTable
                isLoading={loading}
                tableColumns={tableColumns}
                tableData={tableData}
                onItemClick={onItemClick}
                className="impersonate-table"
            />
            {isFetchingNextPage ? (
                <LoadingMoreWrapper>
                    <CircularLoader
                        options={{
                            svg: {
                                stroke: colorsPalettes.carbon[100],
                                strokeWidth: "4",
                                r: 21,
                                cx: "50%",
                                cy: "50%",
                            },
                            style: {
                                width: 46,
                                height: 46,
                            },
                        }}
                    />
                    <EmptyStateSubtitle>{translate("Usermanagement.Modal.Impersonate.Loading")}</EmptyStateSubtitle>
                </LoadingMoreWrapper>
            ) : null}
        </>
    );
};
